import { Component, DestroyRef, OnInit, ViewChild, inject, isDevMode, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, Sort } from '@angular/material/sort';
import { TableSortService } from 'src/app/services/table-sort.service';
import { TableId } from 'src/app/constants/table.constant';
import { MatTableDataSource } from '@angular/material/table';
import { IPackage } from 'src/app/models/packages/package';
import { PackagesService } from 'src/app/services/packages.service';
import { DeleteModalComponent } from 'src/app/components/shared/delete-modal/delete-modal.component';
import { NavigationExtras, Router } from '@angular/router';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { IOrganization } from 'src/app/models/organizations/organization';
import { FormControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { IPreference, IUserProfile } from 'src/app/models/users/userProfile';
import { AuthService } from 'src/app/services/auth.service';
import { Permission } from 'src/app/constants/permission.constant';
import { SignalrService } from 'src/app/services/signalr.service';
import { SelectOrganizationComponent } from 'src/app/components/shared/select-organization/select-organization.component';
import { SignalrGroup } from 'src/app/services/signalr-group';
import { SignalRIdGroups } from 'src/app/constants/signalr.constant';

@Component({
    selector: 'app-package-list',
    templateUrl: './package-list.component.html',
    styleUrls: ['./package-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class PackageListComponent implements OnInit {
  // Service Constructor
  Permission = Permission;
  private readonly destroyRef = inject(DestroyRef);
  private destroy$ = new Subject();
  private listSignalrGroup: SignalrGroup<any> | null = null;

  constructor(
    private router: Router,
    private packagesService: PackagesService,
    private organizationsService: OrganizationsService,
    public dialog: MatDialog,
    public _alert: MatSnackBar,
    public authService: AuthService,
    private signalrService: SignalrService,
    private tableSortService: TableSortService,
  ) {
    this.initialData();
  }
  _currentOrganization: IOrganization | null = null;
  user!: IUserProfile | null;
  currentConfigDate!: IPreference;
  get currentOrganization() {
    return this._currentOrganization;
  }
  set currentOrganization(value: IOrganization | null) {
    if (value != null) {
      if (value.id != null) {
        this._currentOrganization = value;
        this.organizationsService.setCurrentOrg(value);
        this.organizationControl.markAsUntouched();
        this.listSignalrGroup?.leave();
        this.listSignalrGroup = SignalrGroup.joinWithId({
          signalrService: this.signalrService,
          destroyRef: this.destroyRef,
          group: SignalRIdGroups.PackagesList,
          id: value.id!,
          data$: this.signalrService.packagesData,
          onData: (data: any) => {
            if (data.id) {
              const index = this.packages.findIndex((p) => p.id == data.id);
              if (index >= 0) {
                this.packages[index] = data;
                this.dataSource.data = this.packages;
              } else {
                this.packages = [...this.packages, data];
                this.dataSource.data = this.packages;
              }
            }
          },
        });
        this.listPackages(value);
      } else {
        this.organizationControl.markAllAsTouched();
        this.organizationControl.setErrors({
          serverErrors: 'Invalid Organization',
        });
      }
    }
  }
  displayFn(option: IOrganization | null) {
    return option ? option.name : '';
  }
  private _filter(name: string): IOrganization[] {
    const filterValue = name.toLowerCase();
    return this.organizations.filter((option) => option.name.toLowerCase().includes(filterValue));
  }
  parseOrganizationValue(value: any) {
    const name = typeof value === 'string' ? value : value?.name;
    return name ? this._filter(name as string) : this.organizations.slice();
  }

  organizations: IOrganization[] = [];
  organizationControl = new FormControl();
  organizationFilteredOptions!: Observable<IOrganization[]>;
  organizationsSubscription: any;
  loaded: boolean = false;
  currentLoaded: boolean = false;

  packageSubscription: any;
  packages: IPackage[] = [];
  packagesFound: boolean = false;
  errorMessages: string[] = [];

  displayedColumns: string[] = [
    'name',
    'robotPosition',
    'cameraOrientation',
    'externalRefernce',
    'createdAt',
    'Actions',
  ];
  dataSource = new MatTableDataSource<IPackage>();
  @ViewChild(MatPaginator)
  paginator!: MatPaginator;
  @ViewChild(MatSort)
  sort!: MatSort;
  serverErrorMessage: String = '';

  filter: any;
  public applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toUpperCase();
  }

  initialData() {
    this.currentOrganization = this.organizationsService.getCurrentOrg();
    this.currentLoaded = true;
  }

  ngOnInit(): void {
    this.user = this.authService.getAuthInformation();
    this.organizationsSubscription = this.organizationsService.listOrganizations().subscribe(
      (organizations: any) => {
        if (organizations) {
          this.organizations = organizations;
          if (isDevMode()) {
            console.log('[DEV] [PackageListComponent] organizations found: ', organizations);
          }
          this.loaded = true;
          this.organizationFilteredOptions = this.organizationControl.valueChanges.pipe(
            startWith(''),
            map((value) => this.parseOrganizationValue(value)),
          );
        }
      },
      (error) => {
        console.error('[PackageListComponent] error', error);
      },
    );
    this.authService.currentPreference.subscribe((res) => {
      this.currentConfigDate = res;
    });
  }

  listPackages(organization: IOrganization) {
    if (organization.id != null) {
      this.packagesFound = true;
      this.organizationsService.setCurrentOrg(organization);
      this.packageSubscription = this.packagesService.listPackages(organization.id).subscribe(
        (packages) => {
          if (packages) {
            if (isDevMode()) {
              console.log('[DEV] [PackageListComponent] packages found: ', packages);
            }
            this.packages = packages;
            this.dataSource = new MatTableDataSource<IPackage>(this.packages);
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
            this.tableSortService.applySortToMatSort(this.sort, TableId.PACKAGE_LIST);
          }
        },
        (error) => {
          this.packagesFound = false;
          this.serverErrorMessage =
            'There was an error loading the Users from the server. Please restart the system and try again';
        },
      );
    } else {
      if (isDevMode()) {
        console.log('[DEV] [PackageListComponent] fail');
      }
    }
  }

  createPackage() {
    const navigationExtras: NavigationExtras = {
      state: { orgId: this.currentOrganization!.id },
    };
    this.router.navigate(['packages/create'], navigationExtras);
  }

  copyPackage(packageOriginal: IPackage) {
    this.packageSubscription = this.packagesService.detailPackage(packageOriginal.id!).subscribe(
      (_package) => {
        if (_package) {
          const navigationExtras: NavigationExtras = {
            state: { orgId: this.currentOrganization!.id, package: _package },
          };
          this.router.navigate(['packages/create'], navigationExtras);
        }
      },
      (error) => {
        this.serverErrorMessage =
          'There was an error loading the Products from the server. Please restart the system and try again';
        console.error('[PackageListComponent] error', error.message);
      },
    );
  }

  copyPackageAcrossOrganization(packageOriginal: IPackage) {
    const dialogRef = this.dialog.open(SelectOrganizationComponent, {
      data: {
        organizations: this.organizations,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (result.confirmed) {
          this.organizationsService.setCurrentOrg(result.organization);
          this.packageSubscription = this.packagesService
            .detailPackage(packageOriginal.id!)
            .subscribe(
              (_package) => {
                if (_package) {
                  const navigationExtras: NavigationExtras = {
                    state: {
                      orgId: result.organization.id,
                      package: _package,
                    },
                  };
                  this.router.navigate(['packages/create'], navigationExtras);
                }
              },
              (error) => {
                this.serverErrorMessage =
                  'There was an error loading the Products from the server. Please restart the system and try again';
                console.error('[PackageListComponent] error', error.message);
              },
            );
        }
      });
  }

  updatePackage(packageOriginal: IPackage) {
    this.router.navigate([`packages/${packageOriginal.id}/update`]);
  }

  deletePackage(_package: IPackage) {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      data: {
        header: `Delete Package: ${_package.name}`,
        message: 'Are you sure you want to delete this package along with all information?',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.packagesService.deletePackage(_package).subscribe(
          (data) => {
            this._alert.open('Successfully Deleted', 'close', {
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              duration: 2000,
              panelClass: ['success'],
            });
            // Fetch New Products
            this.listPackages(this.currentOrganization!);
          },
          (response) => {
            if (isDevMode()) {
              console.log('[DEV] [PackageListComponent] response', response);
            }
            this._alert.open(
              'Validation Failure: ' + response.error.validationErrors[0].message,
              'close',
              {
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                duration: 10000,
                panelClass: ['error'],
              },
            );
          },
        );
      }
    });
  }

  onSortChange(sort: Sort): void {
    this.tableSortService.setSortState(TableId.PACKAGE_LIST, sort);
  }

  setPageSizePreference(event: any) {
    if (event.pageSize != this.currentConfigDate.pageSize) {
      this.currentConfigDate.pageSize = event.pageSize;
      let payload = {
        name: this.user?.name,
        preference: this.currentConfigDate,
      };
      this.authService.updatePreference(payload).subscribe((res) => {
        if (isDevMode()) {
          console.log('[DEV] [PackageListComponent] res', res);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.signalrService.clearPackages();
  }
}
